import React, { useState } from 'react';
import { toast } from 'sonner';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useReceiptStore } from 'store';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { closeCurrentReceipt } from 'utils/closeReceiptAction';
import { submitDisposition } from 'services/api/deposit';
import { cn } from 'utils/classnames';
import { playErrorSound, playGoodSound } from 'utils/sounds';
import { useAuthStore } from 'store/authStore';
import { useTrackingStore } from 'store/trackingStore';
import { useProductStore } from 'store/productStore';
import { useTranslation } from 'react-i18next';

export default function ConfirmClose() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingProplem, setLoadingProblem] = useState(false);
  const { setCurrentScreen } = useAppStore();
  const { receiptData, setReceiptData } = useReceiptStore();

  const onCancel = () => setCurrentScreen(SCREEN_TYPES.SCAN_BOX);

  const handleClose = async (): Promise<void> => {
    setLoading(true);
    const success = await closeCurrentReceipt();
    if (!success) setLoading(false);
  };

  const handleMoveToProblem = async (): Promise<void> => {
    setLoadingProblem(true);
    try {
      const { user, stationId } = useAuthStore.getState();
      const { setLastTrackingId, setLastRmaId } = useTrackingStore.getState();
      const { setCurrentProduct } = useProductStore.getState();

      const result = await submitDisposition({
        user_id: user?.id ?? '',
        station_id: stationId ?? '',
        transaction_id: receiptData?.transaction_id || receiptData?.receipt?.transaction_id,
        problem_reason_msg: 'Receipt moved to problem team',
      });

      if (result?.responseState === 'confirmed') {
        toast.success(t('confirmClose.moveToProblemSuccess'));
        setCurrentProduct(null);
        setReceiptData(null);
        setLastTrackingId(null);
        setLastRmaId(null);
        setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
        playGoodSound();
      } else {
        toast.error(result?.responseMsg || t('confirmClose.moveToProblemFailedDefault'));
        playErrorSound();
        setLoadingProblem(false);
      }
    } catch {
      toast.error(t('confirmClose.networkError'));
      playErrorSound();
      setLoadingProblem(false);
    }
  };

  const expectedTotal =
    receiptData?.lines?.filter((line) => line && line.expected_ind === 'y').length || 0;
  const actualScanned =
    receiptData?.lines?.filter((line) => line && line.receipt_ind === 'y').length || 0;
  const expectedScanned =
    receiptData?.lines?.filter(
      (line) => line && line.expected_ind === 'y' && line.receipt_ind === 'y'
    ).length || 0;
  const unexpectedScanned =
    receiptData?.lines?.filter(
      (line) => line && line.expected_ind === 'n' && line.receipt_ind === 'y'
    ).length || 0;
  const fraudScanned = 0;

  const isMissingItems = actualScanned < expectedTotal;
  const problemGradient = DISPOSITION_COLORS.PROBLEM.gradient;

  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        isMissingItems && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${problemGradient}`
      )}
    >
      <CardTitle
        className={cn(
          'text-center text-lg font-extrabold tracking-tight',
          isMissingItems ? 'text-white' : 'text-slate-900'
        )}
      >
        {isMissingItems ? t('confirmClose.warningTitle') : t('confirmClose.finishTitle')}
      </CardTitle>

      {isMissingItems && (
        <p className="text-center text-sm font-semibold text-white/80">
          {t('confirmClose.warningMessage')}
        </p>
      )}

      <div
        className={cn(
          'rounded-xl border px-3 py-2 text-xs',
          isMissingItems ? 'border-white/30 bg-white/10' : 'border-slate-200 bg-slate-50'
        )}
      >
        <span className={cn('font-semibold', isMissingItems ? 'text-white/70' : 'text-slate-500')}>
          {t('confirmClose.trackingLabel')}{' '}
        </span>
        <span
          className={cn(
            'font-semibold break-all',
            isMissingItems ? 'text-white' : 'text-slate-900'
          )}
        >
          {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
        </span>
      </div>

      <div
        className={cn(
          'rounded-xl border p-3',
          isMissingItems ? 'border-white/30 bg-white/10' : 'border-slate-200 bg-white shadow-sm'
        )}
      >
        <div
          className={cn(
            'mb-2 text-xs font-semibold uppercase tracking-wide',
            isMissingItems ? 'text-white/70' : 'text-slate-500'
          )}
        >
          {t('confirmClose.summary')}
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Stat label={t('confirmClose.expectedTotal')} value={expectedTotal} warning={isMissingItems} />
          <Stat label={t('confirmClose.actualScanned')} value={actualScanned} warning={isMissingItems} />
          <Stat
            label={t('confirmClose.expectedScanned')}
            value={expectedScanned}
            warning={isMissingItems}
          />
          <Stat label={t('confirmClose.unexpected')} value={unexpectedScanned} warning={isMissingItems} />
          <Stat label={t('confirmClose.fraudScanned')} value={fraudScanned} warning={isMissingItems} />
        </div>
      </div>

      <div
        className={cn('buttons-container flex-col items-center justify-center', {
          '!bg-transparent': !!isMissingItems,
        })}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className={cn(
            'h-11 w-full flex-1 rounded-xl border-2 text-sm font-semibold',
            isMissingItems
              ? 'border-white/60 text-white bg-transparent hover:bg-white/10 hover:text-white'
              : 'border-red-600 text-red-600 hover:bg-red-50'
          )}
        >
          {t('confirmClose.back')}
        </Button>
        <Button
          type="button"
          onClick={handleMoveToProblem}
          disabled={loadingProplem}
          className={cn(
            'h-11 flex-1 w-full rounded-xl bg-white/20 border-2 border-white text-sm font-semibold text-white hover:bg-white/30',
            {
              'bg-red-600 border-red-600 hover:bg-red-700': !isMissingItems,
            }
          )}
        >
          {loadingProplem ? t('confirmClose.processing') : t('confirmClose.moveToProblemTeam')}
        </Button>
        <Button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className={cn('h-11 w-full flex-1 rounded-xl text-sm font-semibold shadow-sm')}
        >
          {loading ? t('confirmClose.processing') : t('confirmClose.finishReturn')}
        </Button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, warning }: { label: string; value: number; warning: boolean }) => (
  <div className="flex items-center justify-between">
    <span className={cn('text-xs', warning ? 'text-white/80' : 'text-slate-600')}>{label}</span>
    <span className={cn('font-bold', warning ? 'text-white' : 'text-slate-900')}>{value}</span>
  </div>
);
